import React, { useEffect, useMemo, useState } from 'react';

import { Checkbox, FormControl, InputLabel, ListSubheader, MenuItem, Select, Stack, TextField } from '@mui/material';

// eslint-disable-next-line unused-imports/no-unused-imports
import BedbankService from '~/services/BedbankService';

interface Props {
  value: string[];
  mode: 'include' | 'exclude';
  onChange: (newValue: string[]) => void;
  onModeChange: (mode: 'include' | 'exclude') => void;
  onFocus?: () => void;
}

const ALL_CHAINS_TOKEN = '__all__';

export default function AccommodationChainNameFilterInput({ value, mode, onChange, onModeChange, onFocus }: Props) {
  const [chainNames, setChainNames] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    BedbankService.getChains().then((res) => setChainNames(res.result));
  }, []);

  const sortedChainNames = useMemo(() => [...chainNames].sort(), [chainNames]);
  const filteredChainNames = useMemo(
    () => sortedChainNames.filter((chainName) => chainName.toLowerCase().includes(searchText.toLowerCase())),
    [searchText, sortedChainNames],
  );

  const selectedFilteredCount = useMemo(
    () => filteredChainNames.filter((chainName) => value.includes(chainName)).length,
    [filteredChainNames, value],
  );

  const isAllFilteredSelected = filteredChainNames.length > 0 && selectedFilteredCount === filteredChainNames.length;

  const handleSelectAllFiltered = () => {
    if (isAllFilteredSelected) {
      onChange(value.filter((chainName) => !filteredChainNames.includes(chainName)));
      return;
    }
    onChange(Array.from(new Set([...value, ...filteredChainNames])));
  };

  const renderValue = () => {
    if (!value.length) {
      return 'Select chain names';
    }
    const maxDisplayed = 2;
    const selectedPreview = value.slice(0, maxDisplayed).join(', ');
    return value.length > maxDisplayed ? `${selectedPreview} +${value.length - maxDisplayed} more` : selectedPreview;
  };

  return (
    <Stack direction="row" spacing={1}>
      <FormControl fullWidth>
        <InputLabel id="chain-filter-label">Chain Name</InputLabel>
        <Select<string[]>
          labelId="chain-filter-label"
          multiple
          value={value}
          label="Chain Name"
          onChange={(event) => {
            const selectedValues = event.target.value as string[];
            if (selectedValues[selectedValues.length - 1] === ALL_CHAINS_TOKEN) {
              handleSelectAllFiltered();
              return;
            }
            onChange(selectedValues);
          }}
          onFocus={onFocus}
          onClose={() => setSearchText('')}
          renderValue={renderValue}
          MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 300 } } }}
        >
          <ListSubheader>
            <TextField
              size="small"
              autoFocus
              placeholder="Search chains..."
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
            />
          </ListSubheader>
          <MenuItem value={ALL_CHAINS_TOKEN}>
            <Checkbox checked={isAllFilteredSelected} />
            Select all filtered
          </MenuItem>
          {filteredChainNames.map((chainName) => (
            <MenuItem key={chainName} value={chainName}>
              <Checkbox checked={value.includes(chainName)} />
              {chainName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 130 }}>
        <InputLabel id="chain-mode-label">Mode</InputLabel>
        <Select
          labelId="chain-mode-label"
          value={mode}
          label="Mode"
          onChange={(e) => onModeChange(e.target.value as 'include' | 'exclude')}
          onFocus={onFocus}
        >
          <MenuItem value="include">Include</MenuItem>
          <MenuItem value="exclude">Exclude</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );
}
